import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useClothingBrowse } from '../hooks/useClothingBrowse';
import { PAGE_SIZE } from '../data/clothing/browseConfig';
import BrowseHero from '../components/clothing/browse/BrowseHero';
import FilterSidebar from '../components/clothing/browse/FilterSidebar';
import BrowseToolbar from '../components/clothing/browse/BrowseToolbar';
import ProductGrid from '../components/clothing/browse/ProductGrid';
import Pagination from '../components/clothing/browse/Pagination';
import GreenButton from '../components/home/GreenButton';

const ClothingBrowsePage: React.FC = () => {
  const browse = useClothingBrowse();
  const { filters, page, loading, error, searchText, submitSearch, clearAll, setPageNumber, activeCount } = browse;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerScrollRef = useRef<HTMLDivElement>(null);

  // Phone "Smart Search" button: open the drawer and focus its Smart Search box.
  // iOS Safari only opens the keyboard when focus() runs synchronously inside the tap, so the
  // focus happens here; the timeout is only a fallback in case focus did not stick.
  const openSmartSearch = useCallback(() => {
    setDrawerOpen(true);
    const scroller = drawerScrollRef.current;
    // The id also exists in the (hidden) desktop sidebar, so look inside the drawer first.
    const findInput = () => scroller?.querySelector<HTMLElement>('#clothing-smart-search')
      ?? document.getElementById('clothing-smart-search');
    const resetAndFocus = () => {
      if (scroller) scroller.scrollTop = 0;
      findInput()?.focus({ preventScroll: true });
    };
    // The drawer is always mounted (only translated off-screen), so the textarea already exists.
    resetAndFocus();
    window.setTimeout(() => {
      const input = findInput();
      if (input && document.activeElement !== input) resetAndFocus();
    }, 320);
  }, []);

  useEffect(() => {
    document.title = 'Custom Clothing | Outpost Custom';
  }, []);

  // Lock scroll while the mobile filter drawer is open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  return (
    <main className="bg-white text-black">
      <BrowseHero searchText={searchText} onSearch={submitSearch} activeTypes={filters.types} total={page?.total ?? null} />

      <div className="max-w-[1500px] mx-auto px-4 md:px-6 pt-2 pb-8 md:py-10 lg:grid lg:grid-cols-[272px_minmax(0,1fr)] lg:gap-10">
        <aside className="hidden lg:block">
          <div className="sticky top-[calc(var(--header-h,0px)+16px)] max-h-[calc(100vh-var(--header-h,0px)-32px)] overflow-y-auto pr-2 -mr-2">
            <div className="flex items-center justify-between pb-2">
              <h2 className="font-montserrat font-extrabold text-[18px] tracking-[-0.01em]">Refine</h2>
              {activeCount > 0 && (
                <button type="button" onClick={clearAll} className="font-poppins text-[12px] font-semibold hover:underline" style={{ color: '#78BE20' }}>
                  Clear all
                </button>
              )}
            </div>
            <FilterSidebar browse={browse} />
          </div>
        </aside>

        <section className="min-w-0">
          <BrowseToolbar browse={browse} onOpenFilters={() => setDrawerOpen(true)} onOpenSmartSearch={openSmartSearch} />

          {error ? (
            <div className="rounded-2xl border border-black/10 p-10 text-center">
              <p className="font-montserrat font-extrabold text-xl">Something went wrong</p>
              <p className="font-poppins text-sm text-black/60 mt-2">{error}</p>
              <div className="mt-6 flex justify-center">
                <GreenButton onClick={() => window.location.reload()}>Try again</GreenButton>
              </div>
            </div>
          ) : !loading && page && page.styles.length === 0 ? (
            <div className="rounded-2xl bg-[#f7f7f7] p-10 md:p-16 text-center">
              <p className="font-montserrat font-extrabold text-2xl tracking-[-0.02em]">No garments match that yet</p>
              <p className="font-poppins text-sm text-black/60 mt-3 max-w-md mx-auto">
                Try fewer filters or a broader search. If you know what you need and cannot see it, tell us and we will source it.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <GreenButton onClick={() => { clearAll(); submitSearch('', true); }}>Clear everything</GreenButton>
                <GreenButton to="/shop" variant="outline">Back to the shop</GreenButton>
              </div>
            </div>
          ) : (
            <>
              <ProductGrid styles={page?.styles || []} loading={loading} pageSize={PAGE_SIZE} />
              {page && <Pagination page={filters.page} totalPages={page.totalPages} onChange={setPageNumber} />}
            </>
          )}
        </section>
      </div>

      {/* Mobile filter drawer */}
      <div className={`lg:hidden fixed inset-0 z-[70] ${drawerOpen ? '' : 'pointer-events-none'}`} aria-hidden={!drawerOpen}>
        <div
          className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${drawerOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setDrawerOpen(false)}
        />
        <div
          className={`absolute inset-y-0 left-0 w-[88vw] max-w-sm bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}`}
          role="dialog"
          aria-label="Filters"
        >
          <div className="flex items-center justify-between px-5 h-14 border-b border-black/10">
            <h2 className="font-montserrat font-extrabold text-[16px]">Refine</h2>
            <button type="button" onClick={() => setDrawerOpen(false)} className="w-9 h-9 rounded-full hover:bg-black/5 flex items-center justify-center" aria-label="Close filters">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div ref={drawerScrollRef} className="flex-1 overflow-y-auto px-5">
            <FilterSidebar browse={browse} />
          </div>
          <div className="p-4 border-t border-black/10 flex gap-3">
            {activeCount > 0 && (
              <button type="button" onClick={clearAll} className="h-11 px-4 border border-black/15 font-poppins text-[12px] font-semibold tracking-[0.1em] uppercase">
                Clear
              </button>
            )}
            <button type="button" onClick={() => setDrawerOpen(false)} className="flex-1 h-11 text-white font-poppins text-[12px] font-semibold tracking-[0.1em] uppercase" style={{ backgroundColor: '#78BE20' }}>
              Show {page ? page.total.toLocaleString('en-GB') : ''} styles
            </button>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ClothingBrowsePage;
